import React, { useId } from 'react'
import { findOption } from 'sutra/components/Select/select-options'
import type {
  SelectCommonProps,
  SelectDomAttributes,
  SelectOption,
  SelectSingleProps,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { useMergedRef } from 'sutra/components/Select/use-merged-ref'
import { useSelectOpen } from 'sutra/components/Select/use-select-open'
import {
  type SelectSearch,
  useSelectSearch,
} from 'sutra/components/Select/use-select-search'
import { useSelectValue } from 'sutra/components/Select/use-select-value'

// What the dispatcher hands a single select: the public props with `onChange`
// widened to cover a clear, plus the pieces `forwardRef`/`pickDomAttributes`
// separate out.
export type SingleSelectProps<
  T extends SelectValue,
  O extends SelectOption<T>,
> = SelectCommonProps<T, O> &
  Omit<SelectSingleProps<T>, 'mode'> & {
    allowClear?: boolean
    onChange?: (value: T | undefined, option: O | undefined) => void
    domAttributes: SelectDomAttributes
    forwardedRef: React.Ref<HTMLElement>
  }

// What lands on the trigger (the `ref`, the caller's `className`/`style`/`id`
// and `aria-*`/`data-*`, and the data attributes the styles key off).
export type SelectTriggerProps = SelectDomAttributes & {
  ref: React.RefCallback<HTMLElement>
  id?: string
  className?: string
  style?: React.CSSProperties
  autoFocus?: boolean
  onBlur?: React.FocusEventHandler<HTMLElement>
  onFocus?: React.FocusEventHandler<HTMLElement>
  'aria-labelledby'?: string
  'data-status'?: string
  'data-variant'?: string
}

export interface SingleSelectState<
  T extends SelectValue,
  O extends SelectOption<T>,
> {
  all: readonly O[]
  selectedOption: O | undefined
  isOpen: boolean
  handleOpenChange: (next: boolean) => void
  commit: (next: T) => void
  clear: () => void
  search: SelectSearch
  triggerProps: SelectTriggerProps
  emptyText: React.ReactNode
  labelId: string
  triggerLabel: React.ReactNode
  showPlaceholder: boolean
  showClear: boolean
  current: T | null | undefined
}

// A value naming no option (options still loading, a stale id) is shown as
// itself, as antd's did, rather than as the placeholder.
const isEmptyValue = (value: SelectValue | null | undefined): boolean =>
  value === null || value === undefined || value === ''

// State and handlers shared by the plain and the searchable single select.
export const useSelectSingle = <
  T extends SelectValue,
  O extends SelectOption<T>,
>(
  props: SingleSelectProps<T, O>,
): SingleSelectState<T, O> => {
  const all = props.options as readonly O[]
  const [current, setCurrent] = useSelectValue<T | null>(
    props.value,
    props.defaultValue,
  )
  const selectedOption = findOption(all, current)
  const { isOpen, setOpen, focusRef } = useSelectOpen<HTMLElement>(props)
  const search = useSelectSearch(props)
  const setTriggerRef = useMergedRef(focusRef, props.forwardedRef)
  const labelId = useId()

  const handleOpenChange = (next: boolean): void => {
    if (next) search.resetQuery()
    setOpen(next)
  }
  const commit = (next: T): void => {
    setCurrent(next)
    props.onChange?.(next, findOption(all, next))
  }
  const clear = (): void => {
    setCurrent(undefined)
    props.onChange?.(undefined, undefined)
    props.onClear?.()
  }

  const hasValue = !isEmptyValue(current)
  const named =
    props.domAttributes['aria-label'] !== undefined ||
    props.domAttributes['aria-labelledby'] !== undefined
  const showPlaceholder =
    props.triggerLabel === undefined &&
    !hasValue &&
    selectedOption === undefined

  // A combobox takes no accessible name from its content (a button did), so
  // the trigger is named by its visible label unless the caller names it.
  const triggerProps: SelectTriggerProps = {
    ref: setTriggerRef,
    id: props.id,
    className: props.className,
    style: props.style,
    autoFocus: props.autoFocus,
    onBlur: props.onBlur,
    onFocus: props.onFocus,
    'aria-labelledby': named ? undefined : labelId,
    'aria-busy': props.loading || undefined,
    'data-status': props.status,
    'data-variant': props.variant,
    ...props.domAttributes,
  }

  const emptyText =
    props.notFoundContent === undefined
      ? props.loading
        ? 'Loading…'
        : 'No matches'
      : props.notFoundContent

  const valueLabel = hasValue ? String(current) : props.placeholder

  return {
    all,
    current,
    selectedOption,
    isOpen,
    handleOpenChange,
    commit,
    clear,
    search,
    triggerProps,
    emptyText,
    labelId,
    triggerLabel: props.triggerLabel ?? selectedOption?.label ?? valueLabel,
    showPlaceholder,
    showClear: Boolean(props.allowClear) && hasValue && !props.disabled,
  }
}
